import { Box, Skeleton, Stack } from '@mui/material';
import DashboardSectionCard from '../DashboardSectionCard';
import DashboardEmptyState from '../DashboardEmptyState';
import { useTranslation } from 'react-i18next';
import { NetworkStatus, useQuery } from '@apollo/client';
import { enqueueSnackbar } from 'notistack';
import { PostsDocument } from '@/graphql/generated/posts.generated';
import { PostGroupBy, PostStage } from '@/graphql/generated/types.generated';
import { PostListDashboard } from '@/components/post-planning/post/PostList';
import { ArticleRounded } from '@mui/icons-material';

const DashboardLatestPosts = () => {
    const { t } = useTranslation();

    const { loading, error, data, networkStatus } = useQuery(PostsDocument, {
        variables: {
            groupBy: PostGroupBy.Stage,
            take: 5,
            stages: Object.values(PostStage).filter((stage) => stage !== PostStage.Archive),
        },
        onError: (error) => enqueueSnackbar(error.message),
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
    });

    const refetching = loading && networkStatus === NetworkStatus.refetch;
    const initialLoad = (loading || !!error) && !refetching;
    const isLoading = loading || !!error;

    const latestPosts = data?.posts.items || [];
    const emptyPosts = !initialLoad && latestPosts.length === 0;

    return (
        <DashboardSectionCard title={t('dashboard.latest-posts.title')}>
            <Stack spacing={2} maxWidth={'100%'} flex={1} maxHeight={320} overflow={'auto'}>
                {emptyPosts && (
                    <DashboardEmptyState
                        icon={ArticleRounded}
                        tone="info"
                        title={t('dashboard.latest-posts.empty.title')}
                        description={t('dashboard.latest-posts.empty.support-text')}
                    />
                )}
                {!initialLoad && latestPosts.length > 0 && (
                    <Stack spacing={2}>
                        {latestPosts.map((post) => (
                            <Box key={post.id}>
                                <PostListDashboard post={post} publicView={true} />
                            </Box>
                        ))}
                    </Stack>
                )}
                {isLoading && (
                    <Stack direction="row" alignItems="center" gap={1.5} height={60}>
                        <Skeleton variant="rounded" height={60} width="100%" sx={{ maxWidth: 60 }} />
                        <Stack width="100%">
                            <Skeleton height={18} width="20%" variant="text" />
                            <Skeleton height={16} width="80%" variant="text" />
                            <Skeleton height={16} width="80%" variant="text" />
                        </Stack>
                    </Stack>
                )}
            </Stack>
        </DashboardSectionCard>
    );
};

export default DashboardLatestPosts;
